import { fromJS } from 'immutable'
import cc from 'classcat'
import slugify from 'speakingurl'

import { generatePageUrl } from '../../../urlGenerators'
import { useTranslate } from '../../../utils/translate'
import SeoSettingsPanel from './SeoSettingsPanel'

type SeoPreviewPanelProps = {
  page: ImmutableMap
  changedPage: ImmutableMap
  shopLocale: string
  shopDefaultLocale: string
  shopTitle: string
  showSeoSettingsForm: boolean
  maybeGenerateNewSlug: (oldPageSlug: string, title: string) => string
  handleEditClick: () => void
  isEditable?: boolean
}

export function SeoPreviewPanel({
  page,
  changedPage,
  shopLocale,
  shopDefaultLocale,
  shopTitle,
  showSeoSettingsForm,
  maybeGenerateNewSlug,
  handleEditClick,
}: Readonly<SeoPreviewPanelProps>) {
  const t = useTranslate('interface', 'components.searchEngineEntryCustomizationComponent')

  const localePrefix = shopLocale !== shopDefaultLocale ? `/${shopLocale.substring(0, 2)}` : ''

  // since .toJS() is too expensive
  const type = changedPage.get('type')
  const slug = changedPage.get('slug')
  const title = changedPage.get('title')
  const titleTag = changedPage.get('titleTag')
  const metaDescription = changedPage.get('metaDescription')

  let href = changedPage.get('href')

  const isHomePage = href === '/'

  const previewPageTitle =
    titleTag || (isHomePage ? shopTitle || title : [title, shopTitle].filter(Boolean).join(' - '))

  // when the user changes the title, update the href accordingly
  if (title !== page.get('title')) {
    const isCategory = type === 'category'
    const newSlug = isCategory
      ? slugify(title, { lang: shopLocale.substring(0, 2), truncate: 250 })
      : maybeGenerateNewSlug(slug, title)
    const parentSlug = !isCategory ? '' : changedPage.getIn(['parents', -1, 'slug']) || ''

    if (!isHomePage) {
      href = generatePageUrl(
        fromJS({
          type,
          slug: (parentSlug && parentSlug + '/') + newSlug,
        }),
      )
    }
  }

  return (
    <div key="seoPanel">
      <div className="ep-form-row">
        <h3 className="ep-form-row-headline">
          {t('title')}
          {!showSeoSettingsForm && (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <a className="ep-form-row-toggle-info" onClick={() => handleEditClick()}>
              {t('editButton.label')}
            </a>
          )}
        </h3>
      </div>
      <div className="ep-form-row">
        <div className="ep-seo-preview-google-styles">
          <h3>{previewPageTitle}</h3>
          <cite>{`${window.location.origin}${localePrefix}${href}`}</cite>
          <span
            className={cc({
              'ep-seo-preview-meta-description-placeholder': !metaDescription,
            })}
          >
            {metaDescription || t('metaDescriptionPlaceholder')}
          </span>
        </div>
      </div>
      {showSeoSettingsForm && <SeoSettingsPanel {...{ previewPageTitle, t }} />}
    </div>
  )
}
